import { toJQuery, type TElementSpecifier } from "@de-xima/xima-common-js-dom";
import {
  deepContains,
  isEmpty,
  modEuclidean,
  notEmpty,
  parseBoolean,
  parseError,
  parseNumber,
  parseString,
} from "@de-xima/xima-common-js-lang";
import $ from "jquery";
import type { IDisposable } from "monaco-editor";

import type { IOverlaySearchbar, IOverlaySearchbarHandler, TOnSearchbarClearListener } from "../core/Header.ts";

const SelectorInput = ".xm-monaco-input";
const SelectorInputBox = ".xm-monaco-inputbox";
const SelectorNext = ".xm-monaco-next";
const SelectorPrev = ".xm-monaco-previous ";
const SelectorClose = ".xm-monaco-close-fw";
const SelectorMatchCount = ".xm-monaco-matchesCount";
const SelectorMatchCase = ".xm-monaco-case-sensitive";
const SelectorMatchWholeWord = ".xm-monaco-whole-word";
const SelectorUseRegexp = ".xm-monaco-regex";
const SelectorCustomCheckbox = ".xm-monaco-custom-checkbox";
const SelectorErrorMessage = ".xm-monaco-inputbox-message";
const SelectorContextView = ".xm-monaco-context-view";

interface ISearchTerm {
  matcher: (value: unknown) => boolean;
  property: string | undefined;
  value: string;
}

class RegExpError extends Error {
  constructor(cause: unknown) {
    super(parseError(cause).message);
  }
}

/**
 * Controller for the `<xi:overlaySearchbar/>` widget. A searchbar for filtering
 * or looking for content in a page. See interface {@link IOverlaySearchbar} for
 * more info.
 */
export class OverlaySearchbar<Candidate, Match> implements IOverlaySearchbar<Candidate, Match> {
  private readonly _container: JQuery;

  private readonly _clearListeners: Set<TOnSearchbarClearListener<Candidate, Match>> = new Set();

  private _handlerSupplier: (() => IOverlaySearchbarHandler<Candidate, Match>) | undefined;

  private _highlightIndex: number | undefined;

  private _matches: Match[] | undefined;

  private _matchingCandidates: Candidate[];

  private _refreshRequested: boolean;

  /**
   * @param container Container with the overlay search bar, i.e. the
   * element with the class `fc-overlay-searchbar`.
   */
  constructor(container: JQuery) {
    this._matches = undefined;

    this._matchingCandidates = [];

    this._highlightIndex = undefined;

    this._container = container;

    this._refreshRequested = false;

    this._handlerSupplier = undefined;

    container.data("widget", this);

    this._init();
  }

  clear(): void {
    const wasEmpty = isEmpty(this.getSearchExpression());
    if (!wasEmpty) {
      this._stopSearch();
    }
    this._setSearchExpression("");
    this._matches = undefined;
    this._matchingCandidates = [];
    this._setHighlighted(undefined);
    this._updatePrevNext();
    this._updateError();
    this._updateMatchCount();
    if (!wasEmpty) {
      for (const listener of this._clearListeners) {
        listener(this);
      }
    }
  }

  focus(): void {
    this.show();
    this._getInput().trigger("focus").trigger("select");
  }

  getElement(): JQuery {
    return this._container;
  }

  getMatchCount(): number {
    return this._matchingCandidates.length;
  }

  getMatches(): Match[] {
    if (this._matches === undefined) {
      this._matches = this._getHandler()?.convertCandidatesToMatches(this, this._matchingCandidates) ?? [];
    }
    return this._matches;
  }

  getSearchExpression(): string {
    return String(this._getInput().val());
  }

  hide(): void {
    if (this.isVisible() && this._isClosable()) {
      this._container.addClass("hidden");
      this.clear();
      this._trigger("hide");
    }
  }

  isAutoHighlight(): boolean {
    return String(this._container.data("autoHighlight")) === "true";
  }

  isMatchCase(): boolean {
    return this._container.find(SelectorMatchCase).hasClass("checked");
  }

  isMatchWholeWord(): boolean {
    return this._container.find(SelectorMatchWholeWord).hasClass("checked");
  }

  isUseRegexp(): boolean {
    return this._container.find(SelectorUseRegexp).hasClass("checked");
  }

  isVisible(): boolean {
    return !this._container.hasClass("hidden");
  }

  position(coordinates: JQueryUI.JQueryPositionOptions): void {
    this._container.position(coordinates);
  }

  refreshSearch(): void {
    this._setHighlighted(undefined);
    this._matchingCandidates = [];
    this._matches = undefined;
    try {
      this._matchingCandidates = this._findAllMatches();
      this._updateError();
    } catch (e) {
      if (!(e instanceof RegExpError)) {
        console.error("Search failed", e);
      }
      this._updateError(parseError(e).message);
      this._matchingCandidates = [];
    }
    this._updateMatchCount();
    this._updatePrevNext();
    if (this.isAutoHighlight()) {
      this._gotoNextMatch(1);
    }
    this._trigger("search");
  }

  requestRefresh(): void {
    this._refreshRequested = true;
    if (this.isVisible()) {
      setTimeout(() => this._handleRefreshRequest(), 20);
    }
  }

  setHandler(handler: IOverlaySearchbarHandler<Candidate, Match>): void {
    this._handlerSupplier = () => handler;
  }

  setHandlerSupplier(handlerSupplier: () => IOverlaySearchbarHandler<Candidate, Match>): void {
    this._handlerSupplier = handlerSupplier;
  }

  show(): void {
    if (!this.isVisible()) {
      this._container.removeClass("hidden");
      this._trigger("show");
    }
  }

  onClear(listener: TOnSearchbarClearListener<Candidate, Match>): IDisposable {
    this._clearListeners.add(listener);
    return { dispose: () => this.offClear(listener) };
  }

  offClear(listener: TOnSearchbarClearListener<Candidate, Match>): void {
    this._clearListeners.delete(listener);
  }

  private _stopSearch() {
    this._getHandler()?.stopSearch(this);
  }

  private _setHighlighted(index: number | undefined) {
    if (this._highlightIndex === index) {
      return;
    }
    if (this._highlightIndex !== undefined && this._matches !== undefined) {
      const match = this._matches[this._highlightIndex];
      if (match !== undefined) {
        this._getHandler()?.leaveMatch(this, match, this._highlightIndex);
      }
    }
    this._highlightIndex = index;
  }

  private _setSearchExpression(search: string): void {
    this._getInput().val(search);
  }

  private _getHandler(): IOverlaySearchbarHandler<Candidate, Match> | undefined {
    return this._handlerSupplier?.();
  }

  /**
   * Returns a predicate that takes the text of a candidate and checks whether
   * the text matches the search term. Creates a matcher for the current search
   * options.
   * @private
   * @param search Search term against which to match the values.
   * @returns Predicate that checks whether the value matches the search
   * term.
   */
  private _createSearchMatcher(search: string): (value: unknown) => boolean {
    const matchCase = this.isMatchCase();
    // TODO Implement match whole word
    // const matchWholeWord = this.isMatchWholeWord();
    const useRegexp = this.isUseRegexp();
    if (useRegexp) {
      try {
        const regexp = new RegExp(search, matchCase ? undefined : "i");
        return (value) => value !== undefined && regexp.test(String(value));
      } catch (e) {
        throw new RegExpError(e);
      }
    } else {
      if (matchCase) {
        return (value) => value !== undefined && String(value).indexOf(search) >= 0;
      }
      return (value) => value !== undefined && String(value).toLowerCase().indexOf(search.toLowerCase()) >= 0;
    }
  }

  /**
   * Performs a full search with the current search term and the current search
   * options. Returns all matches that were found. No side effects.
   * @returns A list of all matching candidates that were found, i.e. those
   * candidates that match the search expression. They still need to be
   * converted to matches via `handler#convertCandidatesToMatches`.
   */
  private _findAllMatches(): Candidate[] {
    const handler = this._getHandler();
    if (handler === undefined) {
      console.warn("No search handler was specified, cannot perform search.");
      return [];
    }

    // Parse search expression.
    const searches = this._getParsedSearchTerm();

    // Do not search when nothing was entered.
    if (searches.length === 0) {
      return [];
    }

    // Find all candidates and filter them by the search term.
    const candidates = handler.getCandidates(this);
    const matchingCandidates = candidates.filter((candidate, candidateIndex) => {
      return searches.every((search) => {
        const text = handler.getSearchText(this, candidate, candidateIndex, search.property) ?? "";
        return deepContains(text, search.matcher);
      });
    });

    return matchingCandidates;
  }

  /**
   * @returns The element for the HTML input for the search expression.
   */
  private _getInput(): JQuery {
    return this._container.find(SelectorInput);
  }

  /**
   * Takes the currently entered search expression and parses it into a list of
   * search terms. Each search term consists of a property and value.
   * @returns List of parsed search terms.
   */
  private _getParsedSearchTerm(): ISearchTerm[] {
    const raw = this.getSearchExpression();
    const terms = raw.split(/\s+/);
    return terms
      .filter((term) => term.length > 0)
      .map((term) => {
        const colon = term.indexOf(":");
        const property = term.substring(0, colon).trim();
        const value = term.substring(colon + 1).trim();
        return {
          matcher: this._createSearchMatcher(value ?? ""),
          property: property ?? undefined,
          value: value ?? "",
        };
      })
      .filter((term) => term.value.length > 0);
  }

  /**
   * Moves the next or previous match.
   * @param direction 1 for the next match, -1 for the previous match.
   * @returns The match to which we moved.
   */
  private _gotoNextMatch(direction: -1 | 1): Match | undefined {
    const handler = this._getHandler();
    const matches = this.getMatches();
    if (matches.length === 0) {
      this._setHighlighted(undefined);
      return undefined;
    }

    const index = handler?.findNextMatch(this, matches, this._highlightIndex ?? 0, direction) ?? 0;
    // Find next match
    const resolvedIndex =
      this._highlightIndex === undefined
        ? index !== undefined
          ? index
          : 0
        : index !== undefined
          ? index
          : this._highlightIndex + direction;
    const newIndex = modEuclidean(resolvedIndex, matches.length);
    this._setHighlighted(newIndex);
    const match = matches[newIndex];
    if (handler !== undefined && match !== undefined) {
      handler.gotoMatch(this, match, newIndex);
    }

    this._updateMatchCount();

    return match;
  }

  /**
   * Performs the refresh asynchronously to the invoking method. When a request to refresh the search is made
   * multiple times during one event loop cycle, they are batched into one call.
   */
  private _handleRefreshRequest(): void {
    if (this._refreshRequested) {
      this._refreshRequested = false;
      this.refreshSearch();
    }
  }

  private _init(): void {
    // Filter options
    if (!this._isEnableMatchCase()) {
      this._container.find(SelectorMatchCase).hide();
    }
    if (!this._isEnableMatchWholeWord()) {
      this._container.find(SelectorMatchWholeWord).hide();
    }
    if (!this._isEnableUseRegexp()) {
      this._container.find(SelectorUseRegexp).hide();
    }

    // Events
    this._container.find(SelectorInput).on("change", () => this._onInputChange());
    this._container.find(SelectorInput).on("keyup", (e) => this._onInputKeyup(e));
    this._container.find(SelectorNext).on("click", (e) => this._onClickNext(e));
    this._container.find(SelectorPrev).on("click", (e) => this._onClickPrev(e));
    this._container.find(SelectorClose).on("click", (e) => this._onClickClose(e));
    this._container.find(SelectorCustomCheckbox).on("click", (e) => this._onClickCustomCheckbox(e));

    // Moving
    if (this._isDraggable()) {
      this._container.draggable({
        handle: ".xm-monaco-move",
      });
    }

    // Resizing
    if (this._isResizable()) {
      const minWidth = Math.max(240, this._getMinWidth());
      const maxWidth = Math.max(minWidth, this._getMaxWidth());
      this._container.resizable({
        handles: {
          w: this._container.find(".xm-monaco-sash"),
        },
        maxWidth: maxWidth,
        minWidth: minWidth,
      });
    }
  }

  /**
   * @returns Whether the option for making the search case-sensitive is available.
   */
  private _isEnableMatchCase(): boolean {
    return parseBoolean(this._container.attr("data-enable-match-case"));
  }

  /**
   * @returns Whether the option for making the searchbar draggable is enabled.
   */
  private _isDraggable(): boolean {
    return parseBoolean(this._container.attr("data-draggable"));
  }

  /**
   * @returns Whether the option for making the searchbar resizable is enabled.
   */
  private _isResizable(): boolean {
    return parseBoolean(this._container.attr("data-resizable"));
  }

  /**
   * @returns Whether the option for making the searchbar closable is enabled.
   */
  private _isClosable(): boolean {
    return parseBoolean(this._container.attr("data-closable"));
  }

  /**
   * @returns Whether the option for matching only whole word is available.
   */
  private _isEnableMatchWholeWord(): boolean {
    return parseBoolean(this._container.attr("data-enable-match-whole-word"));
  }

  /**
   * @returns Whether the option for using RegExp's is available.
   */
  private _isEnableUseRegexp(): boolean {
    return parseBoolean(this._container.attr("data-enable-use-regexp"));
  }

  /**
   * @returns Max width in pixels for resizing.
   */
  private _getMaxWidth(): number {
    return parseNumber(this._container.attr("data-max-width"));
  }

  /**
   * @returns Min width in pixels for resizing.
   */
  private _getMinWidth(): number {
    return parseNumber(this._container.attr("data-min-width"));
  }

  /**
   * When the close button was clicked. Closes the searchbar and hides it.
   * @param event Click event from pressing the button.
   */
  private _onClickClose(event: JQuery.TriggeredEvent): void {
    const target = $(event.target);
    if (target.hasClass("disabled")) {
      return;
    }
    this.hide();
  }

  /**
   * When one of the checkboxes with the search options (case-sensitivity,
   * whole-word only, enabled regexp was clicked). Updates the UI state and
   * refreshes the search.
   * @param event Click event from the click on the checkbox.
   */
  private _onClickCustomCheckbox(event: JQuery.TriggeredEvent): void {
    const target = $(event.target);
    const isChecked = target.hasClass("checked");
    const type = parseString(target.data("type"));
    if (isChecked) {
      target.removeClass("checked");
      this._trigger("uncheck", type);
    } else {
      target.addClass("checked");
      this._trigger("check", type);
    }
    this.refreshSearch();
  }

  /**
   * When the "go to next match" button was pressed. Selects the next match and
   * highlights it.
   * @param event Click event from pressing the button
   */
  private _onClickNext(event: JQuery.TriggeredEvent): void {
    const target = $(event.target);
    if (target.hasClass("disabled")) {
      return;
    }
    this._gotoNextMatch(1);
  }

  /**
   * When the "go to previous match" button was pressed. Selects the previous
   * match and highlights it.
   * @param event Click event from pressing the button
   */
  private _onClickPrev(event: JQuery.TriggeredEvent): void {
    const target = $(event.target);
    if (target.hasClass("disabled")) {
      return;
    }
    this._gotoNextMatch(-1);
  }

  /**
   * When the text in the search bar changed. Updates the search results.
   */
  private _onInputChange() {
    this.refreshSearch();
    // Stop search when input was cleared
    if (isEmpty(this.getSearchExpression())) {
      this._stopSearch();
    }
  }

  private _onInputKeyup(event: JQuery.TriggeredEvent): void {
    // Escape, hide the search bar
    if (event.key === "Escape" || event.keyCode === 27) {
      if (this._isClosable()) {
        this.hide();
      } else {
        this.clear();
      }
      event.stopPropagation();
      return;
    }
    // Enter, same as clicking on the next button
    if (event.key === "Enter" || event.keyCode === 13) {
      this._gotoNextMatch(1);
      event.stopPropagation();
      return;
    }
    // Actual input
    this.refreshSearch();
    // Stop search when input was cleared
    if (isEmpty(this.getSearchExpression())) {
      this._stopSearch();
    }
  }

  /**
   * Triggers an event with the given name on the container of this searchbar.
   * It is triggered with its own namespace.
   * @param name Name of the event to trigger.
   * @param args Optional arguments.
   */
  private _trigger(name: string, ...args: unknown[]) {
    this._container.trigger(`searchbar.${name}`, args);
  }

  /**
   * Takes the error message and displays it to the user. If no message is
   * given, hides the error message box.
   * @param message The error message to show. If not given or empty, hides the
   * error message box.
   */
  private _updateError(message?: string | undefined): void {
    if (notEmpty(message)) {
      this._container.find(SelectorInputBox).addClass("xm-monaco-error");
      this._container.find(SelectorErrorMessage).text(message);
      this._container.find(SelectorContextView).removeClass("xm-monaco-empty");
    } else {
      this._container.find(SelectorInputBox).removeClass("xm-monaco-error");
      this._container.find(SelectorErrorMessage).text("");
      this._container.find(SelectorContextView).addClass("xm-monaco-empty");
    }
  }

  /**
   * Updates the text of the match count that shows how many items the search
   * expression matches.
   */
  private _updateMatchCount(): void {
    const matchCount = this._container.find(SelectorMatchCount);
    const length = this.getMatchCount();
    const highlightIndex = this._highlightIndex;
    if (length === 0) {
      matchCount.text(parseString(this._container.data("i18nNoResults")));
    } else if (highlightIndex !== undefined) {
      const resultIndex = parseString(this._container.data("i18nResultIndex"));
      const replaced = resultIndex.replace("{current}", String(highlightIndex + 1)).replace("{total}", String(length));
      matchCount.text(replaced);
    } else if (length === 1) {
      matchCount.text(parseString(this._container.data("i18nResultCountSingle")));
    } else {
      const countMultiple = parseString(this._container.data("i18nResultCountMultiple"));
      const replaced = countMultiple.replace("{count}", String(length));
      matchCount.text(replaced);
    }
  }

  /**
   * Updates the buttons for going to the previous and next match. When no
   * matches were found, the buttons are disabled.
   */
  private _updatePrevNext(): void {
    const next = this._container.find(SelectorNext);
    const prev = this._container.find(SelectorPrev);
    if (this.getMatchCount() > 0) {
      next.removeClass("disabled");
      prev.removeClass("disabled");
      next.attr("aria-disabled", "false");
      prev.attr("aria-disabled", "false");
    } else {
      next.addClass("disabled");
      prev.addClass("disabled");
      next.attr("aria-disabled", "true");
      prev.attr("aria-disabled", "true");
    }
  }

  /**
   * Initializes the overlay searchbar widget on the given container element.
   * @typeParam Candidate Type of the candidates when searching.
   * @typeParam Match Type of the final match results.
   * @param container Container element with the `fc-overlay-searchbar` style
   * class.
   * @returns A new overlay searchbar instance for interacting with the
   * searchbar programmatically, or the existing searchbar instance when the
   * searchbar was already initialized.
   */
  static init<Candidate, Match>(container: TElementSpecifier<HTMLElement>): IOverlaySearchbar<Candidate, Match> {
    const $container = toJQuery(container);
    const widget = $container.data("widget") as unknown;
    if (typeof widget === "object" && widget instanceof OverlaySearchbar) {
      return widget;
    }
    return new OverlaySearchbar($container);
  }
}
